<template>
  <q-header elevated>
    <CSToolbar>
      <span class="lt-md">
        <q-btn flat @click="toggleLeftDrawer" round dense icon="menu" />
      </span>
      <q-toolbar-title class="lt-md">Help A Family</q-toolbar-title>

      <q-toolbar-title class="gt-sm" v-if="showMenuOptions">
        <q-btn-group flat v-if="loggedSideOptions">
          <CSButton to="/" dense flat color="text-white">
            <!-- <span class="q-px-xs">Home</span> -->
            <div class="row no-wrap">
              <CSIcon
                left
                name="fas fa-home"
                color="text-white"
                size="1.3em"
                class="self-center"
              />
              <div>Home</div>
            </div>
          </CSButton>
          <span v-for="menu in MENU_ITEMS">
            <CSButton
              v-if="menu.subMenus === undefined"
              color="text-white"
              flat
              :to="menu.menuLink"
            >
              <div class="row no-wrap">
                <CSIcon
                  left
                  :name="menu.icon"
                  color="text-white"
                  size="1.3em"
                  class="self-center"
                />
                <div>{{ menu.menuTitle }}</div>
              </div>
            </CSButton>
            <q-btn-dropdown v-else :key="menu.menuTitle" flat>
              <template #label>
                <div class="row no-wrap">
                  <CSIcon
                    left
                    :name="menu.icon"
                    color="text-white"
                    size="1.3em"
                    class="self-center"
                  />
                  <div class="text-center">{{ menu.menuTitle }}</div>
                </div>
              </template>
              <q-list>
                <q-item
                  v-for="subMenu in menu.subMenus"
                  :key="subMenu.menuTitle"
                  :to="subMenu.menuLink"
                  clickable
                  v-ripple
                  v-close-popup
                >
                  <q-item-section side>
                    <CSIcon :name="subMenu.icon" size="1.3em" />
                  </q-item-section>
                  <q-item-section>
                    <q-item-label> {{ subMenu.menuTitle }} </q-item-label>
                  </q-item-section>
                </q-item>
              </q-list>
            </q-btn-dropdown>
          </span>
        </q-btn-group>
      </q-toolbar-title>

      <q-btn v-if="showMenuOptions" outline label="logout" @click="onLogout" />
    </CSToolbar>
  </q-header>

  <q-drawer v-model="leftDrawer" :width="250" :breakpoint="500" bordered>
    <!-- <q-scroll-area class="fit"> -->
    <span v-if="showMenuOptions">
      <CSButton to="/" align="left" flat class="full-width" color="text-white">
        <div class="row no-wrap">
          <CSIcon left name="fas fa-home" size="1.3em" class="self-center" />
          <div>Home</div>
        </div>
      </CSButton>
      <span v-for="menu in MENU_ITEMS">
        <CSButton
          v-if="menu.subMenus === undefined"
          class="full-width"
          align="left"
          color="text-white"
          flat
          :to="menu.menuLink"
        >
          <div class="row no-wrap">
            <CSIcon left :name="menu.icon" size="1.3em" class="self-center" />
            <div>{{ menu.menuTitle }}</div>
          </div>
        </CSButton>
        <q-btn-dropdown
          v-else
          :key="menu.menuTitle"
          align="between"
          class="full-width"
          fill
          flat
        >
          <template #label>
            <div class="row no-wrap">
              <CSIcon left :name="menu.icon" size="1.3em" />
              <span class="q-px-xs">{{ menu.menuTitle }}</span>
            </div>
          </template>
          <q-list>
            <q-item
              v-for="subMenu in menu.subMenus"
              :key="subMenu.menuTitle"
              :to="subMenu.menuLink"
              clickable
              v-ripple
              v-close-popup
            >
              <q-item-section side>
                <CSIcon :name="subMenu.icon" size="1.3em" />
              </q-item-section>
              <q-item-section>
                <q-item-label>{{ subMenu.menuTitle }}</q-item-label>
              </q-item-section>
            </q-item>
          </q-list>
        </q-btn-dropdown>
      </span>
    </span>

    <!-- </q-scroll-area> -->
  </q-drawer>
</template>

<script setup lang="ts">
import { onMounted, ref, watch, computed } from "vue";
import { useQuasar } from "quasar";

import CSToolbar from "@/components/CharitySystem/CSToolbar.vue";
import CSButton from "@/components/CharitySystem/CSButton.vue";
import CSIcon from "./CharitySystem/CSIcon.vue";
import SecurityUseCase from "@/usecases/SecurityUseCase";
import { showErrorNotification } from "./shared/dialog";
import { useRouter } from "vue-router";
import store from "@/store";
import { MenuItem, filterMenu } from "@/shared/MenuGenerator";
import { UserSecurityDetail } from "@/models/APIModels/UserSecurityDetail";
import { processResponse } from "@/shared/helperFunctions";
import { UserCategoryEnum } from "@/shared/ModelEnums";

const $q = useQuasar();
const router = useRouter();

const securityUseCase = new SecurityUseCase();

const leftDrawer = ref(false);

const editProfileLink = ref("#");

const loggedSideOptions = ref(false);

const enableEdit = computed(() => store.state.user?.userStatusCd === "US_A");

const showMenuOptions = computed(() => {
  const result =
    store.state.user && store.state.user.schoolInvitationTypeCd == undefined;
  return result;
});

watch(
  () => store.state.user,
  async (newValue, _) => {
    await updateMenu(newValue);
  },
);

onMounted(async () => await updateMenu(store.state.user));

async function updateMenu(user?: UserSecurityDetail) {
  if (user != undefined) {
    editProfileLink.value = getLink(user.userCategoryCd, user.userReference);

    const result = await securityUseCase.getUserPermissions();

    MENU_ITEMS.value = filterMenu(
      result,
      user.userCategoryCd,
      user.userRoleCd,
      user.userReference,
    );
    loggedSideOptions.value = true;
  } else {
    loggedSideOptions.value = false;
    MENU_ITEMS.value = [];
  }
}

const MENU_ITEMS = ref<MenuItem[]>();

function toggleLeftDrawer() {
  leftDrawer.value = !leftDrawer.value;
}

async function onLogout() {
  try {
    const result = await securityUseCase.logout();
    processResponse(result, () => {
      store.commit("mutClearCurrentUser");
      router.replace("/security/login");
    });
  } catch (ex) {
    if (ex instanceof Error) $q.notify(showErrorNotification(ex.message));
  }
}

watch(
  () => $q.screen.width,
  () => {
    reactOnScreenSize();
  },
);

function reactOnScreenSize(): void {
  if (leftDrawer.value && $q.screen.gt.sm) {
    leftDrawer.value = false;
  }
}

function getLink(userCategory: UserCategoryEnum, userReference: string) {
  switch (userCategory) {
    case UserCategoryEnum.UC_A:
      return `/administrator/update/${userReference}`;
    case UserCategoryEnum.UC_S:
      return `/schoolOfficer/update?sor=${userReference}`;
    case UserCategoryEnum.UC_D:
      return `/donor/update/privatedonor`;

    default:
      return "#";
  }
}
</script>
